import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import combineActions from '../../../utils/combineActions';

import sportsApi from '../../../utils/sportsApi';

import Tooltip from '@app/ui/sports-components/Tooltip';

import getFilters from '../../../actions/filters/getFilters';
import updatePlacementSponsorAssociation from '../../../actions/settings/updatePlacementSponsorAssociation';
import addPlacement from '../../../actions/settings/addPlacement';
import updatePlacement from '../../../actions/settings/updatePlacement';
import addPlacementImage from '../../../actions/settings/addPlacementImage';
import sendNotification from '../../../actions/notifications/send';

import getCount from '../../../utils/getCount';
import validateDisplayName from '../../../utils/validateDisplayName';
import { Context } from '../../contexts/OnboardingContext';
import { PlacementContext } from '../../contexts/PlacementContext';

const ConfirmedButtons = ({
    client,
    getFilters,
    sendNotification,
    updatePlacementSponsorAssociation,
    addPlacementImage,
    addPlacement: addOnboardingPlacement,
    updatePlacement: updateOnboardingPlacement,
}) => {
    const { t } = useTranslation('settings');
    const {
        rhId,
        isLoading,
        structureLocked,
        canAddPlacementSponsors,
        setDraggableActive: setShowSponsorUploader,
        currentSelectedPlacement,
        setCurrentSelectedPlacement,
        sponsorsToAdd,
        setSponsorsToAdd,
        sponsorsToRemove,
        setSponsorsToRemove,
        placements,
        maxPlacements,
        hasStandardPlacements,
    } = useContext(Context);

    const {
        showPlacementUploader,
        setShowPlacementUploader,
        placementTypeOptions,
        isNew,
        setIsNew,
        placementTypeId,
        setPlacementTypeId,
        files,
        setFiles,
        setPrevFiles,
        rhPls,
        alias,
        displayName,
        setDisplayName,
        isEditingName,
        setIsEditingName,
    } = useContext(PlacementContext);

    const [isSaving, setSaving] = useState(false);

    const examples =
        (currentSelectedPlacement && currentSelectedPlacement.examples) || [];

    const hasDrawnBB = [...examples, ...files].reduce((acc, ex) => {
        acc = acc && ex.boundingBoxes && ex.boundingBoxes.length > 0;
        return acc;
    }, true);

    const hasMaxPlacements = !!maxPlacements;

    useEffect(() => {
        resetPlacement();
    }, [rhId]);

    const updateSponsors = ({
        placement,
        association,
        sponsorIds,
    }) => async () => {
        try {
            const { id: placementId } = placement;
            const payload = {
                association,
                entityIds: [placementId],
            };

            const updateSponsor = async sponsorId => {
                try {
                    if (association === 'add')
                        await sportsApi
                            .url(
                                `/rightsholders/${rhId}/sponsors/${sponsorId}/placements`,
                            )
                            .json(payload)
                            .post()
                            .json();

                    await sportsApi
                        .url(
                            `/clients/${client.id}/rightsholders/${rhId}/sponsors/${sponsorId}/placements`,
                        )
                        .json(payload)
                        .post()
                        .json();

                    if (association === 'remove') {
                        const crsps = await sportsApi
                            .url(`/client-rightsholder-sponsor-placements`)
                            .query({
                                rightsholderId: rhId,
                                sponsorId,
                                placementId,
                                noClientId: true,
                            })
                            .get()
                            .json();

                        if (Array.isArray(crsps) && crsps.length > 0) {
                            // second argument is used to delete
                            return [sponsorId, false];
                        }
                    }
                    return [sponsorId, true];
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log(e);
                }
            };

            const promises = sponsorIds.map(sponsorId =>
                updateSponsor(sponsorId),
            );

            const updatedSponsors = await Promise.all(promises);

            if (Array.isArray(updatedSponsors))
                return updatedSponsors.filter(us => !!us);
            return [];
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const saveSponsors = async ({ placement }) => {
        try {
            if (!placement) return;

            const toAdd = sponsorsToAdd.reduce((acc, sp) => {
                const mustKeep =
                    sponsorsToRemove.findIndex(s => s.id === sp.id) < 0 ||
                    getCount({
                        el: sp,
                        arr: sponsorsToRemove,
                    }) <
                        getCount({
                            el: sp,
                            arr: sponsorsToAdd,
                        });

                if (mustKeep) {
                    const isAdded = acc.findIndex(s => s.id === sp.id) > -1;
                    if (!isAdded) {
                        acc.push(sp);
                    }
                }

                return acc;
            }, []);

            const toRemove = sponsorsToRemove.reduce((acc, sp) => {
                const mustKeep =
                    sponsorsToAdd.findIndex(s => s.id === sp.id) < 0 ||
                    getCount({
                        el: sp,
                        arr: sponsorsToRemove,
                    }) >
                        getCount({
                            el: sp,
                            arr: sponsorsToAdd,
                        });

                if (mustKeep) {
                    const isRemoved = acc.findIndex(s => s.id === sp.id) > -1;
                    if (!isRemoved) {
                        acc.push(sp);
                    }
                }

                return acc;
            }, []);

            if (toAdd.length > 0) {
                const sponsorIds = toAdd.reduce((acc, el) => {
                    acc.push(el.id);
                    return acc;
                }, []);
                await updateSponsors({
                    association: 'add',
                    sponsorIds,
                    placement,
                })();
            }

            let removedSponsors = [];

            if (toRemove.length > 0) {
                const sponsorIds = toRemove.reduce((acc, el) => {
                    acc.push(el.id);
                    return acc;
                }, []);
                removedSponsors = await updateSponsors({
                    association: 'remove',
                    sponsorIds,
                    placement,
                })();
            }

            const sps = placement.sponsors
                ? [...toAdd, ...placement.sponsors]
                : toAdd;

            const newSponsors = sps.reduce((acc, sp) => {
                if (
                    removedSponsors.findIndex(
                        ([id, shouldDelete]) => id === sp.id && shouldDelete,
                    ) > -1
                )
                    return acc;
                if (
                    removedSponsors.findIndex(
                        ([id, shouldDelete]) => id === sp.id && !shouldDelete,
                    ) > -1
                ) {
                    acc.push({
                        ...sp,
                        isNew: false,
                        active: false,
                        clientRemoved: true,
                    });
                } else {
                    acc.push({ ...sp, isNew: false });
                }
                return acc;
            }, []);

            await updatePlacementSponsorAssociation({
                rhId,
                plId: placement.id,
                sponsors: newSponsors,
            });

            return newSponsors;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
        }
    };

    const addBB = async ({ placementId, example, boxes }) => {
        try {
            const endPoint = `/clients/${client.id}/rightsholders/${rhId}/placements/${placementId}/examples/${example.id}/bounding-boxes`;

            const domainObject = boxes;

            const payload = {
                action: 'createMultiple',
                domainObject,
            };
            const boundingBoxes = await sportsApi
                .url(endPoint)
                .post(payload)
                .json();

            return { ...example, boundingBoxes };
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const savePlacement = async () => {
        const placementData = isNew
            ? await postPlacement()
            : currentSelectedPlacement;

        if (!placementData) return;
        if (files.length && files.length > 0) {
            const examples = await Promise.all(
                files
                    .filter(f => f.isNew)
                    .map(f => postExampleImage(placementData, f, isNew)),
            );

            const newExamplePromises = examples
                .filter(
                    f =>
                        f.isNew &&
                        f.boundingBoxes &&
                        f.boundingBoxes.length > 0,
                )
                .reduce((acc, ex) => {
                    acc.push(
                        addBB({
                            placementId: placementData.id,
                            example: ex,
                            boxes: ex.boundingBoxes,
                        }),
                    );
                    return acc;
                }, []);

            const newExamples = await Promise.all(newExamplePromises);

            if (placementData.examples && placementData.examples.length > 0) {
                placementData.examples = [
                    ...placementData.examples,
                    ...newExamples,
                ];
            } else {
                placementData.examples = [...newExamples];
            }
        }
        setFiles([]);
        setPrevFiles([]);

        // Only validate display name if edit button has been clicked
        if (isEditingName && hasStandardPlacements) {
            const errorMessage = validateDisplayName(displayName);

            if (!errorMessage) {
                await postDisplayName(displayName, placementData.id);
                placementData.name = displayName;

                sendNotification(t('clientConfiguration.onboarding.notifications.displayNameSaved'), {
                    context: 'success',
                });
            } else {
                sendNotification(errorMessage, {
                    context: 'warning',
                });
            }
            setIsEditingName(false);
        }

        return placementData;
    };

    const save = async () => {
        if (isNew && !placementTypeId) {
            sendNotification(t('clientConfiguration.onboarding.notifications.selectPlacementType'), {
                context: 'warning',
            });
            return;
        }
        if (!hasDrawnBB) {
            sendNotification(
                t('clientConfiguration.onboarding.notifications.completeBB'),
                {
                    context: 'warning',
                },
            );
            return;
        }
        setSaving(true);
        try {
            const placement = await savePlacement();

            if (!placement) return;

            const sponsors = await saveSponsors({ placement });
            if (sponsors) placement.sponsors = sponsors;
            if (isNew) {
                await getFilters();
                await addOnboardingPlacement({ rhId, placement });
            } else {
                await updateOnboardingPlacement({ rhId, placement });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
        setShowPlacementUploader(false);
        setFiles([]);
        setPrevFiles([]);
        setPlacementTypeId(null);
        setDisplayName('');
        setShowSponsorUploader(false);
        setCurrentSelectedPlacement(null);
        setSaving(false);
    };

    const currentPlacementId = currentSelectedPlacement
        ? currentSelectedPlacement.id
        : null;

    useEffect(() => {
        setSponsorsToAdd([]);
        setSponsorsToRemove([]);
    }, [currentPlacementId]);

    const placementType = placementTypeOptions.find(
        p => p.value === placementTypeId,
    );

    const postPlacement = async () => {
        try {
            const rhPlWithPlacementType =
                Array.isArray(rhPls) &&
                rhPls.find(
                    pl =>
                        pl.placementType &&
                        pl.placementType.id === placementTypeId,
                );

            const name = rhPlWithPlacementType
                ? rhPlWithPlacementType.name
                : alias;

            const getRhPlMatchingName = rhPlWithPlacementType => {
                if (!rhPlWithPlacementType) {
                    if (Array.isArray(rhPls)) {
                        return rhPls.find(
                            pl =>
                                pl.name &&
                                pl.name.toUpperCase() === name.toUpperCase(),
                        );
                    }
                } else {
                    return rhPlWithPlacementType;
                }
            };

            const rhPlMatchingName = getRhPlMatchingName(rhPlWithPlacementType);

            const getRightsholderPlacement = async rhPlMatchingName => {
                if (rhPlMatchingName) {
                    return rhPlMatchingName;
                } else {
                    const payloadData = {
                        action: 'create',
                        domainObject: {
                            name: name,
                            placementType: { id: placementTypeId },
                        },
                    };

                    return await sportsApi
                        .url(`/rightsholders/${rhId}/placements`)
                        .json(payloadData)
                        .post()
                        .json();
                }
            };

            const rightsholderPlacement = await getRightsholderPlacement(
                rhPlMatchingName,
            );

            const payloadData = {
                action: 'add',
                placementIds: [rightsholderPlacement.id],
            };

            await sportsApi
                .url(`/clients/${client.id}/rightsholders/${rhId}/placements`)
                .json(payloadData)
                .post()
                .json();

            return {
                ...rightsholderPlacement,
                active: true,
                clientRemoved: false,
            };
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const postExampleImage = async (parentPlacement, image, isNew) => {
        const payloadData = {
            action: 'create',
            domainObject: {
                imageUrl: image.url,
            },
        };
        setSaving(true);
        const example = await sportsApi
            .url(
                `/clients/${client.id}/rightsholders/${rhId}/placements/${parentPlacement.id}/examples`,
            )
            .json(payloadData)
            .post()
            .json();

        if (!isNew)
            await addPlacementImage({
                rhId,
                plId: parentPlacement.id,
                example,
            });
        return {
            ...example,
            boundingBoxes: image.boundingBoxes || [],
            isNew: image.isNew || false,
        };
    };

    const postDisplayName = async (cName, plId) => {
        const payloadData = {
            customName: cName,
        };

        try {
            await sportsApi
                .url(
                    `/clients/${client.id}/rightsholders/${rhId}/placements/${plId}`,
                )
                .json(payloadData)
                .put()
                .json();
        } catch (e) {
            sendNotification(e.message, {
                context: 'warning',
            });
        }
    };

    const resetPlacement = () => {
        setShowPlacementUploader(false);
        setFiles([]);
        setPrevFiles([]);
        setPlacementTypeId(null);
        setShowSponsorUploader(false);
        setCurrentSelectedPlacement(null);
    };

    const cancelPlacement = () => {
        const confirmation = !structureLocked
            ? confirm(t('clientConfiguration.onboarding.cancelConfirmPlacement'))
            : true;
        if (confirmation) {
            resetPlacement();
        }
    };

    const addPlacement = async () => {
        setPlacementTypeId(null);
        setShowPlacementUploader(true);
        setIsNew(true);
    };

    const showPlacementInfo = !!currentSelectedPlacement;
    const canSavePlacementChanges =
        !structureLocked || (!isNew && canAddPlacementSponsors);
    const reachedCapacity =
        hasMaxPlacements &&
        placements.filter(p => p.active && !p.clientRemoved).length >=
            maxPlacements;

    return (
        <div className="gds-flex__item gds-flex__item--grow-0">
            {!showPlacementUploader && !hasStandardPlacements && (
                <Tooltip
                    text={
                        reachedCapacity
                            ? t('clientConfiguration.onboarding.capacityReachedPlacements')
                            : ''
                    }
                    position="top-right"
                >
                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--primary"
                        onClick={addPlacement}
                        disabled={structureLocked || isLoading || reachedCapacity}
                    >
                        {t('clientConfiguration.onboarding.addPlacement')}
                    </button>
                </Tooltip>
            )}
            {showPlacementUploader && (
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--default -m-r-1"
                        onClick={cancelPlacement}
                        disabled={isSaving || isLoading}
                    >
                        {t('clientConfiguration.onboarding.cancel')}
                    </button>

                    <button
                        type="button"
                        className="gds-button gds-button--sm gds-button--secondary"
                        onClick={save}
                        disabled={
                            !canSavePlacementChanges ||
                            isSaving ||
                            isLoading ||
                            (!showPlacementInfo &&
                                (!placementType || !placementType.name))
                        }
                    >
                        {t('clientConfiguration.onboarding.saveChanges')}
                    </button>
                </div>
            )}
        </div>
    );
};

ConfirmedButtons.propTypes = {
    client: PropTypes.object,
    updatePlacementSponsorAssociation: PropTypes.func,
    addPlacementImage: PropTypes.func,
    addPlacement: PropTypes.func,
    getFilters: PropTypes.func,
    sendNotification: PropTypes.func,
    updatePlacement: PropTypes.func,
};

export default connect(
    ['user', 'client'],
    combineActions({
        getFilters,
        addPlacement,
        addPlacementImage,
        updatePlacementSponsorAssociation,
        sendNotification,
        updatePlacement,
    }),
)(ConfirmedButtons);
